import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useCompileRuns, useOverview } from "../lib/hooks";

export const Route = createFileRoute("/typescript-analysis")({
  component: TypeScriptAnalysisPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: "overview" | "browse" } => {
    return {
      tab: search.tab === "browse" ? "browse" : "overview",
    };
  },
});

interface TypeScriptError {
  msg: string;
  severity: string;
  loc: string;
}

interface CompileRunWithErrors {
  compileId: string;
  timestamp: string;
  userId?: string;
  projectId?: string;
  honcVersion?: string;
  errorCount: number;
  errorsJson?: string;
  r2Prefix?: string;
  legacy: boolean;
  errors: TypeScriptError[];
}

const ProjectIdLink = ({ projectId }: { projectId?: string }) => {
  if (!projectId) {
    return <span className="text-gray-400">N/A</span>;
  }

  // Try to create a link to the project if it looks like a numeric ID
  if (/^\d+$/.test(projectId)) {
    return (
      <a
        href={`https://app.fiberplane.com/projects/${projectId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-600 hover:text-indigo-800 underline"
      >
        {projectId}
      </a>
    );
  }

  return <span>{projectId}</span>;
};

function TypeScriptAnalysisPage() {
  // Get auth state from route context (populated by root route beforeLoad)
  const { auth } = Route.useRouteContext();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  const activeTab = tab || "overview";
  const [filters, setFilters] = useState({
    userId: "",
    projectId: "",
    hasErrors: "true", // Default to show only errors
  });

  // Use the custom hooks for data fetching
  const { data: overview, isLoading: overviewLoading } = useOverview();
  const { data: runs, isLoading: runsLoading } = useCompileRuns({
    page: 1,
    limit: 20,
    ...filters,
  });

  const formatDate = (timestamp: string | number) => {
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatShortDate = (timestamp: string | number) => {
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Show loading state while data is loading
  if (overviewLoading && !overview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation */}
      <div className="mb-8">
        <nav className="flex space-x-8">
          {["overview", "browse"].map((tab) => (
            <Link
              key={tab}
              to="/typescript-analysis"
              search={{ tab: tab as "overview" | "browse" }}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab === "browse" ? "Browse Runs" : tab}
            </Link>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && overview && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-bold">R</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Runs
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {overview.statistics.totalRuns}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-bold">✓</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Successful
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {overview.statistics.successfulRuns}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-bold">%</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Success Rate
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {overview.statistics.completionRate}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-bold">!</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Errors
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {overview.statistics.totalErrors}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity Chart */}
          {overview.recentActivity.length > 0 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Recent Activity (Last 7 Days)
                </h3>
              </div>
              <div className="px-4 py-4">
                <div className="grid grid-cols-7 gap-2">
                  {overview.recentActivity.map((day) => (
                    <div key={day.date} className="text-center">
                      <div className="text-xs text-gray-500 mb-1">
                        {new Date(day.date).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                      <div className="bg-blue-100 rounded p-2">
                        <div className="text-sm font-medium text-blue-900">
                          {day.count} runs
                        </div>
                        <div className="text-xs text-red-600">
                          {day.errors} errors
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Browse Tab */}
      {activeTab === "browse" && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Filter Compilation Runs
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label
                    htmlFor="userId"
                    className="block text-sm font-medium text-gray-700"
                  >
                    User ID
                  </label>
                  <input
                    id="userId"
                    type="text"
                    value={filters.userId}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        userId: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="gh:username"
                  />
                </div>
                <div>
                  <label
                    htmlFor="projectId"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Project ID
                  </label>
                  <input
                    id="projectId"
                    type="text"
                    value={filters.projectId}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        projectId: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="project-123"
                  />
                </div>
                <div>
                  <label
                    htmlFor="hasErrors"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Status Filter
                  </label>
                  <select
                    id="hasErrors"
                    value={filters.hasErrors}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        hasErrors: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">All Runs</option>
                    <option value="true">Errors Only</option>
                    <option value="false">Successful Only</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => {
                      // This will trigger a re-fetch with new filters
                    }}
                    className="w-full py-2 px-4 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {runs && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Compilation Runs ({runs.pagination.total} total)
                </h3>
              </div>
              <div className="border-t border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Compile ID
                        </th>
                        <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          When
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Project ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Errors
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {runs.runs.map((run) => (
                        <tr key={run.compileId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Link
                              to="/compile-run/$compileId"
                              params={{ compileId: run.compileId }}
                              className="text-sm font-mono text-indigo-600 hover:text-indigo-900 underline"
                            >
                              {run.compileId.slice(0, 8)}...
                            </Link>
                          </td>
                          <td
                            className="px-2 py-3 whitespace-nowrap w-32"
                            title={formatDate(run.timestamp)}
                          >
                            <span className="text-xs text-gray-500">
                              {formatShortDate(run.timestamp)}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {run.userId || "N/A"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            <ProjectIdLink projectId={run.projectId} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                run.errorCount > 0
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {run.errorCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                run.errorCount === 0
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {run.errorCount === 0 ? "Success" : "Failed"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Loading state for runs */}
          {runsLoading && (
            <div className="bg-white shadow sm:rounded-lg p-6">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                <span className="ml-2 text-gray-600">Loading runs...</span>
              </div>
            </div>
          )}

          {/* No results state */}
          {!runsLoading && runs && runs.runs.length === 0 && (
            <div className="bg-white shadow sm:rounded-lg p-6">
              <div className="text-center">
                <p className="text-gray-500">
                  No compilation runs found matching your filters.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setFilters({ userId: "", projectId: "", hasErrors: "" })
                  }
                  className="mt-2 text-indigo-600 hover:text-indigo-500"
                >
                  Clear filters
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
